/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect, useRef, useState } from "react";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDAlert from "components/MDAlert";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "@mui/material/Table";
import DataTable from "../../../examples/Tables/DataTable";
import data from "../../dashboard/components/Projects/data";
import useApi from "../../../components/Utils/api";
import MDInput from "../../../components/MDInput";
import toast from "react-hot-toast";
import subDays from "date-fns/subDays";
import startOfMonth from "date-fns/startOfMonth";
import addMonths from "date-fns/addMonths";
import endOfMonth from "date-fns/endOfMonth";
import { Dropdown, SelectPicker } from 'rsuite';
import { afterToday } from "rsuite/cjs/DateRangePicker/disabledDateUtils";
import reportsLineChartData from "../../dashboard/data/reportsLineChartData";
import Papa from 'papaparse';

const getCurrentYear = () => new Date().getFullYear();

const generateYearOptions = (startYear, endYear) => {
  const years = [];
  for (let year = startYear; year <= endYear; year++) {
    years.push({ label: year.toString(), value: year });
  }
  return years;
};

const getMonths = () => {
  const months = [];
  for (let i = 0; i < 12; i++) {
    const date = new Date(0, i);
    months.push({
      label: date.toLocaleString('default', { month: 'long' }),
      value: i,
    });
  }
  return months;
};
function GrossSettlementReport() {
  const { grossSettlementReportsColumns } = data();
  const { predefinedRanges } = reportsLineChartData;
  const [summary, setSummary] = useState([]);
  const [selectedYear, setSelectedYear] = useState(getCurrentYear());
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [dateRange, setDateRange] = useState(null);
  const api = useApi();
  const formatDate = (date) => {
    const isoDate = new Date(date).toISOString();
    return isoDate.slice(0, 10); // Extracts only the date part
  };
  const handleYearChange = (year) => {
    setSelectedYear(year);
    if (selectedMonth != null) {
      updateDateRange(year, selectedMonth);
    }
  };

  const handleMonthChange = (month) => {
    setSelectedMonth(month);
    updateDateRange(selectedYear, month);
  };
  const updateDateRange = (year, month) => {
    const firstDayOfMonth = new Date(year, month, 1);
    const lastDayOfMonth = new Date(year, month + 1, 0);
    setDateRange([firstDayOfMonth, lastDayOfMonth]);
  };
  const getSettlementReport = (lastMonthDateRange) => {
    if(lastMonthDateRange == null) return;
    setDateRange(lastMonthDateRange);
    const data = {
      fromDate : formatDate(lastMonthDateRange[0]),
      toDate : formatDate(lastMonthDateRange[1]),
    }
    api.post("reports/monthlyGrossSettlementReport", data)
      .then((response) => {
        if(response.data.statusCodeValue === 200) {
          setSummary(response.data.body);
        }else {
          const toastId = toast.error("No Record Found", { autoClose: false }); // Prevents auto-closing
          setTimeout(() => toast.dismiss(toastId), 1000); // Dismisses the toast after 1 second
        }
      })
      .catch((error) => {
        if (error.response && error.response.status === 403) {
          toast.error("You do not have permission to perform this action.");
          return;
        }
        const toastId = toast.error("No Record Found", { autoClose: false }); // Prevents auto-closing
        setTimeout(() => toast.dismiss(toastId), 1000); // Dismisses the toast after 1 second
      })
  }

  const generateCSV = () => {
    if (summary.length === 0) {
      return '';
    }

    const headers = grossSettlementReportsColumns.map(column => column.Header);

    const summaryWithUppercaseColumns = summary.map(item => {
      return grossSettlementReportsColumns.map(column => {
        const accessor = column.accessor;
        const value = typeof accessor === 'function' ? accessor(item) : item[accessor];
        return value;
      });
    });

    const csvData = [headers, ...summaryWithUppercaseColumns];

    const csv = Papa.unparse(csvData, {
      quotes: true,
    });

    return csv;
  };

  const handleDownloadCSV = () => {
    if (summary.length === 0) {
   
const toastId = toast.error("No records found to download", { autoClose: false }); // Prevents auto-closing
setTimeout(() => toast.dismiss(toastId), 1000); // Dismisses the toast after 1 second

      return;
    }

    const csvData = generateCSV();

    const currentDate = new Date().toISOString().slice(0, 10);
    const currentTime = new Date().toLocaleTimeString().replace(/:/g, '-');
    const dateTimeString = `${currentDate}_${currentTime}`;

    const fileName = `GrossSettlementReport_${dateTimeString}.csv`;

    const blob = new Blob([csvData], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };


  useEffect(() => {
    if(dateRange !== null){
      getSettlementReport(dateRange);
    }
  },[dateRange]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mt={6} mb={3}>
        <Grid container spacing={3} justifyContent="center">
          <Grid item xs={12} lg={12}>
            <Grid item xs={12} md={12} lg={12}>
              <MDBox mb={3} textAlign={"center"}>
                <SelectPicker
                    data={generateYearOptions(getCurrentYear() - 10, getCurrentYear())}
                    onChange={handleYearChange}
                    value={selectedYear}
                    placeholder="Select Year"
                />
                <SelectPicker
                    data={getMonths()}
                    onChange={handleMonthChange}
                    placeholder="Select Month"
                    disabled={selectedYear === null}
                />
              </MDBox>
            </Grid>
            <MDButton
              variant="gradient"
              onClick={handleDownloadCSV}
              color="info"
              style={{ marginRight: "24px", marginBottom: "12px" }}
            >
              Export
            </MDButton>
            <DataTable
              table={{ columns : grossSettlementReportsColumns, rows : summary }}
              showTotalEntries={true}
              isSorted={true}
              noEndBorder
              entriesPerPage={true}
              canSearch={true}
            />
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default GrossSettlementReport;
